import {
    ScanSearch,
    Megaphone,
    Clapperboard,
    Sparkles,
    Languages,
    KeyRound,
    Filter,
    Gauge,
    BarChart3,
} from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const SERVICES = [
    {
        icon: ScanSearch,
        title: "Influencer Sourcing & Vetting",
        desc: "Human-vetted discovery across niche demographics, verified for authenticity and audience fit.",
        badge: "Influencer",
        dot: "bg-crimson",
        span: "lg:col-span-3",
    },
    {
        icon: Megaphone,
        title: "Meta & Paid Social Scaling",
        desc: "Full-funnel campaign setup, audience targeting and retargeting across Meta platforms.",
        badge: "Performance",
        dot: "bg-flame",
        span: "lg:col-span-3",
    },
    {
        icon: Clapperboard,
        title: "Briefing & Content Production",
        desc: "End-to-end creator briefing and content production, tracked per creator.",
        badge: "Creative",
        dot: "bg-blue-500",
        span: "lg:col-span-2",
    },
    {
        icon: Sparkles,
        title: "Creator Content as Ad Creative",
        desc: "Top-performing creator content adapted and tested as paid ad creative.",
        badge: "Creative",
        dot: "bg-blue-500",
        span: "lg:col-span-2",
    },
    {
        icon: Languages,
        title: "Regional-Language Casting",
        desc: "Regional-language and niche-specific casting for markets big and small.",
        badge: "Reach",
        dot: "bg-emerald-500",
        span: "lg:col-span-2",
    },
    {
        icon: KeyRound,
        title: "Usage Rights & Whitelisting",
        desc: "Usage rights and whitelisting negotiated up front, so paid media can run on creator handles.",
        badge: "Rights",
        dot: "bg-violet-500",
        span: "lg:col-span-3",
    },
    {
        icon: Filter,
        title: "Full-Funnel Targeting & Retargeting",
        desc: "Creator interactions become custom audience pools for bottom-funnel conversions.",
        badge: "Funnel",
        dot: "bg-flame",
        span: "lg:col-span-3",
    },
    {
        icon: Gauge,
        title: "Media Optimization & ROAS",
        desc: "Continuous A/B testing and spend reallocation toward what is actually working.",
        badge: "Performance",
        dot: "bg-flame",
        span: "lg:col-span-2",
    },
    {
        icon: BarChart3,
        title: "Transparent Reporting",
        desc: "Clear dashboards on spend, CPA / ROAS and creative performance. Reach and engagement tracked per creator.",
        badge: "Analytics",
        dot: "bg-blue-500",
        span: "lg:col-span-4",
    },
];

export default function Services() {
    return (
        <section id="services" data-testid="services-section" className="bg-paper py-16 sm:py-24">
            <div className="mx-auto max-w-6xl px-5 sm:px-8">
                <Reveal className="mb-12 max-w-3xl">
                    <Eyebrow tone="light">What we do</Eyebrow>
                    <h2 className="mt-4 font-syne text-3xl font-bold tracking-tight text-ink sm:text-5xl">
                        One engine.
                        <br />
                        Two disciplines.
                    </h2>
                    <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                        Influencer marketing and performance marketing under one roof — so
                        creator content and paid media stop pulling in opposite directions.
                    </p>
                </Reveal>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-6">
                    {SERVICES.map((s, i) => (
                        <Reveal
                            key={s.title}
                            delay={(i % 3) * 0.08}
                            className={`${s.span} h-full`}
                            testid={`service-card-${i + 1}`}
                        >
                            <div className="group flex h-full flex-col rounded-2xl border border-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200">
                                <div className="mb-5 flex items-center justify-between">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-mist text-ink transition-colors duration-300 group-hover:bg-ink group-hover:text-white">
                                        <s.icon className="h-5 w-5" />
                                    </div>
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
                                        <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                                        {s.badge}
                                    </span>
                                </div>
                                <h3 className="font-syne text-lg font-bold tracking-tight text-ink sm:text-xl">
                                    {s.title}
                                </h3>
                                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">
                                    {s.desc}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
